import { Injectable, inject } from "@angular/core";
import { Observable, from, of } from "rxjs";
import { PrefixDiagnosticModel } from "src/app/backend/models/configuration/prefix";
import { RequestComment } from "src/app/backend/models/request-comment/request-comment";
import { RequestCommentCreate } from "src/app/backend/models/request-comment/request-comment.create";
import { RequestCommentDelete } from "src/app/backend/models/request-comment/request-comment.delete";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { UUID, uuid } from "src/app/support/uuid";
import {
  Request,
  RequestExam
} from "src/app/modules/bootstrap/rocket-ui/backend/models/request";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { omitBy, isUndefined } from "lodash";
import { switchMap, map } from "rxjs/operators";
import { writeDemoStorage } from "src/app/support/demo-storage";
import { UserNotify } from "src/app/support/notify-decorator";
import { omitFalsyProperties, swapDeepProperty } from "src/app/support/object";
import { Patient } from "src/app/modules/bootstrap/rocket-ui/backend/models/patient";
import { RequestLoadTags } from "src/app/modules/bootstrap/rocket-ui/backend/models/request/request.load-tags";
import { RequestUpdateTags } from "src/app/modules/bootstrap/rocket-ui/backend/models/request/request.update-tags";
import { Study } from "src/app/modules/bootstrap/rocket-ui/backend/models/study";
import { UserModel } from "src/app/modules/bootstrap/rocket-ui/backend/models/user";

@Injectable()
export class RequestBackend {
  readonly #http = inject(HttpClient);

  load(request: PageRequest): Observable<PaginatedResult<Request>> {
    return this.#http.get<PaginatedResult<Request>>(
      `${environment.snoots.api.diagnostic}/requests`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );
  }

  loadByPatient(
    patientId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Request>> {
    return this.#http.get<PaginatedResult<Request>>(
      `${environment.snoots.api.diagnostic}/requests`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            patientId: patientId,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );
  }

  loadByCustomer(
    customerId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Request>> {
    return this.#http.get<PaginatedResult<Request>>(
      `${environment.snoots.api.diagnostic}/requests`,
      {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            customerId: customerId,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );
  }

  list(
    request: PageRequest,
    filters?: { customerName?: string }
  ): Observable<PaginatedResult<Request>> {
    return this.#http.get<PaginatedResult<Request>>(
      `${environment.snoots.api.diagnostic}/requests`,
      {
        params: omitFalsyProperties({
          p: request.page,
          s: request.pageSize,
          customerName: filters?.customerName
        })
      }
    );
  }

  loadRequestById(filterId: string): Observable<Request> {
    return this.#http.get<Request>(
      `${environment.snoots.api.diagnostic}/requests/${filterId}`
    );
  }

  loadRequestByIdLight(filterId: string): Observable<Request> {
    return this.#http.get<Request>(
      `${environment.snoots.api.diagnostic}/requests/${filterId}`,
      {
        params: { light: true }
      }
    );
  }

  @UserNotify({
    // successMessage: marker('Richiesta creata'),
    errorMessage: marker("Non è stato possibile creare l'ordine di lavoro")
  })
  create(data: any): Observable<Request> {
    return this.#http.post<Request>(
      `${environment.snoots.api.diagnostic}/requests`,
      data,
      {
        headers: {
          "snt-product": "rocket"
        }
      }
    );
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro aggiornato"),
    errorMessage: marker("Non è stato possibile aggiornare l'ordine di lavoro")
  })
  update(data: Partial<Request>, id: string): Observable<Request> {
    return this.#http.patch<Request>(
      `${environment.snoots.api.diagnostic}/requests/${id}`,
      swapDeepProperty("id", "_id", data)
    );
  }

  @UserNotify({
    successMessage: marker("Worklist creata"),
    errorMessage: marker("Non è stato possibile creare la worklist")
  })
  createRequestExam(id: string, data: any): Observable<RequestExam> {
    return this.#http.post<RequestExam>(
      `${environment.snoots.api.diagnostic}/requests/${id}/exams`,
      data,
      {
        headers: {
          "snt-product": "rocket"
        }
      }
    );
  }

  @UserNotify({
    // successMessage: marker('Studio creato'),
    errorMessage: marker("Non è stato possibile aggiornare lo studio")
  })
  createStudies(id: string, data: any): Observable<Study> {
    return this.#http.post<Study>(
      `${environment.snoots.api.diagnostic}/requests/${id}/studies`,
      data
    );
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro cancellato"),
    errorMessage: marker("Non è stato possibile cancellare l'ordine di lavoro")
  })
  delete(requestId: string): Observable<string> {
    return this.#http.delete<string>(
      `${environment.snoots.api.diagnostic}/requests/${requestId}`
    );
  }

  @UserNotify({
    successMessage: marker("Ordine di lavoro rifiutato"),
    errorMessage: marker("Non è stato possibile rifiutare l'ordine di lavoro")
  })
  reject(requestId: string, reason: string): Observable<string> {
    return this.#http.put<string>(
      `${environment.snoots.api.diagnostic}/requests/${requestId}/reject`,
      {
        rejectionReason: reason
      }
    );
  }

  customer(): Observable<UserModel[]> {
    return of([]);
  }

  customerById(customerId: string): Observable<UserModel> {
    return of(<any>{});
  }

  patient(): Observable<Patient[]> {
    return of(<any>{});
  }

  patientById(patientId: string): Observable<Patient> {
    return of(<any>{});
  }

  // @UserNotify({
  //   // successMessage: marker('Richiesta accettata'),
  //   errorMessage: marker("Non è stato possibile accettare l'ordine di lavoro")
  // })
  // accept(requestId: string): Observable<string> {
  //   return this.http.post<string>(
  //     `${environment.snoots.api.diagnostic}/requests/${requestId}/accept`,
  //     {},
  //     {
  //       headers: {
  //         "snt-product": "rocket"
  //       }
  //     }
  //   );
  // }

  loadComments(): Observable<RequestComment[]> {
    return from(Promise.resolve([]));
  }

  loadCommentsByRequest({
    requestUuid
  }: {
    requestUuid: UUID;
  }): Observable<RequestComment[]> {
    return from(Promise.resolve([]));
  }

  @UserNotify({
    successMessage: marker("Messaggio creato"),
    errorMessage: marker("Non è stato possibile creare il messaggio")
  })
  createComment(request: RequestCommentCreate): Observable<void> {
    return this.loadComments().pipe(
      switchMap((list) => {
        const requestComment: RequestComment = {
          uuid: uuid(),
          author: "Mario Rossi",
          date: dateUtil().toISOString(),
          text: request.text
        };

        return from(writeDemoStorage("", [...list, requestComment]));
      })
    );
  }

  @UserNotify({
    successMessage: marker("Messaggio rimosso"),
    errorMessage: marker("Non è stato possibile rimuovere il messaggio")
  })
  deleteComment(request: RequestCommentDelete): Observable<void> {
    return this.loadComments().pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            "",
            list.filter((x) => {
              return x.uuid !== request.uuid;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Tag aggiornati"),
    errorMessage: marker("Non è stato possibile aggiungere il tag")
  })
  updateTags(id: string, request: RequestUpdateTags): Observable<Request> {
    return this.#http.patch<Request>(
      `${environment.snoots.api.diagnostic}/requests/${id}`,
      request
    );
  }

  loadTags(request: RequestLoadTags): Observable<string[]> {
    const customer = this.#http.get<Request>(
      `${environment.snoots.api.diagnostic}/requests/${request.id}`
    );

    return customer.pipe(map((request) => request.tags || []));
  }

  @UserNotify({
    successMessage: marker("Update prefix"),
    errorMessage: marker("Non è stato possibile aggiornare il prefix")
  })
  updatePrefix(
    request: PrefixDiagnosticModel
  ): Observable<PrefixDiagnosticModel> {
    return this.#http.patch<PrefixDiagnosticModel>(
      `${environment.snoots.api.diagnostic}/prefixes`,
      request
    );
  }
}
